// Contact form service for The Frosting Room
import { 
  addDoc, 
  collection, 
  serverTimestamp,
  getFirestore 
} from 'firebase/firestore';
import { db } from './firebase';
import { v4 as uuidv4 } from 'uuid';
import { sendContactEmail } from './customEmailService';
import { ContactFormData, ContactSubmission } from '../types';

// Email template interface
export interface EmailTemplate {
  to: string[];
  subject: string;
  html: string;
  text: string;
}

// Contact form collection reference
const contactCollection = collection(db, 'contactSubmissions');

/**
 * Submit a contact form and store it in the database
 * Also sends email notifications to the specified recipients
 */
export const submitContactForm = async (formData: ContactFormData): Promise<ContactSubmission> => {
  try {
    console.log('📧 Submitting contact form:', formData);

    // Create contact submission document
    const contactSubmission: Omit<ContactSubmission, 'id' | 'createdAt' | 'updatedAt'> = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone?.trim() || null, // Use null instead of undefined for Firebase
      message: formData.message.trim(),
      status: 'new',
      ipAddress: await getClientIP(),
      userAgent: navigator.userAgent
    };

    // Add to Firestore
    const docRef = await addDoc(contactCollection, {
      ...contactSubmission,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log('✅ Contact form saved to database with ID:', docRef.id);

    // Send email notifications using custom email service
    await sendContactEmailNotifications(formData);

    // Return the created submission
    const submission: ContactSubmission = {
      id: docRef.id,
      ...contactSubmission,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return submission;
  } catch (error) {
    console.error('❌ Error submitting contact form:', error);
    throw new Error('Failed to submit contact form. Please try again.');
  }
};

/**
 * Send email notifications using Firebase Functions backend
 */
const sendContactEmailNotifications = async (formData: ContactFormData): Promise<void> => {
  try {
    console.log('📧 Sending contact form email using Firebase Functions...');
    
    // Use Firebase Functions backend
    const emailResult = await sendContactEmail({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || undefined,
      message: formData.message
    });
    
    if (emailResult.success) {
      console.log('✅ Contact form email sent successfully via Firebase Functions');
    } else {
      console.error('❌ Failed to send contact form email:', emailResult.error);
      throw new Error(emailResult.error || 'Email sending failed');
    }
  } catch (error) {
    console.error('❌ Error sending contact form emails:', error);
    // Don't throw error here - we still want to save the form even if email fails
    console.warn('⚠️ Contact form was saved but email notifications failed');
  }
};


/**
 * Create HTML content for contact form email
 */
const createContactFormHtml = (formData: ContactFormData): string => {
  const currentDate = new Date().toLocaleString('en-IE', { 
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>New Contact Form Submission</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background-color: #f8f9fa;
        }
        .email-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          border: 1px solid #e0e0e0;
        }
        .header {
          background: linear-gradient(135deg, #FFE5E5 0%, #FFD1DC 100%);
          color: #1A1A2E;
          padding: 30px 20px;
          text-align: center;
        }
        .header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #1A1A2E;
        }
        .header p {
          margin: 5px 0 0 0;
          color: #2D2D44;
          font-weight: 500;
        }
        .content {
          padding: 30px 20px;
          background: white;
        }
        .intro-text {
          color: #1A1A2E;
          margin-bottom: 25px;
          font-size: 16px;
          text-align: center;
        }
        .contact-info {
          background: #FFF0F0;
          padding: 25px;
          border-radius: 8px;
          margin: 25px 0;
          border-left: 4px solid #FFB3B3;
          border: 1px solid #e0e0e0;
        }
        .field {
          margin-bottom: 20px;
        }
        .field:last-child {
          margin-bottom: 0;
        }
        .label {
          font-weight: 600;
          color: #1A1A2E;
          display: block;
          margin-bottom: 8px;
          font-family: inherit;
        }
        .value {
          color: #2D2D44;
          padding: 12px 15px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e0e0e0;
          font-size: 15px;
        }
        .submission-info {
          margin-top: 25px;
          padding: 15px;
          background: #f0f8ff;
          border-radius: 6px;
          border-left: 4px solid #2196f3;
          font-size: 14px;
          color: #666;
        }
        .footer {
          background: #1A1A2E;
          color: white;
          padding: 25px 20px;
          text-align: center;
          font-size: 13px;
        }
        .footer p {
          margin: 8px 0;
        }
        .footer .business-name {
          font-weight: 600;
          font-size: 16px;
          color: #FFE5E5;
        }
        .footer .location {
          color: #FFD1DC;
        }
        a {
          color: #FFB3B3;
          text-decoration: none;
          font-weight: 500;
        }
        a:hover {
          text-decoration: underline;
          color: #FFE5E5;
        }
        .footer a {
          color: #FFD1DC;
        }
        @media (max-width: 600px) {
          body {
            padding: 10px;
          }
          .header, .content, .footer {
            padding: 20px 15px;
          }
          .contact-info {
            padding: 20px 15px;
          }
          .header h1 {
            font-size: 20px;
          }
        }
      </style>
    </head>
    <body>
      <div class="email-container">
        <div class="header">
          <h1>New Contact Form Submission</h1>
          <p>The Frosting Room</p>
        </div>
        
        <div class="content">
          <p class="intro-text">You have received a new message through the contact form on The Frosting Room website.</p>
          
          <div class="contact-info">
            <div class="field">
              <span class="label">Name:</span>
              <div class="value">${formData.name}</div>
            </div>
            
            <div class="field">
              <span class="label">Email:</span>
              <div class="value">
                <a href="mailto:${formData.email}">${formData.email}</a>
              </div>
            </div>
            
            ${formData.phone ? `
            <div class="field">
              <span class="label">Phone:</span>
              <div class="value">
                <a href="tel:${formData.phone}">${formData.phone}</a>
              </div>
            </div>
            ` : ''}
            
            <div class="field">
              <span class="label">Message:</span>
              <div class="value" style="white-space: pre-wrap;">${formData.message}</div>
            </div>
          </div>
          
          <div class="submission-info">
            <strong>Submitted:</strong> ${currentDate}
          </div>
        </div>
        
        <div class="footer">
          <p class="business-name">The Frosting Room</p>
          <p class="location">Dublin, Ireland</p>
          <p>This message was sent from the contact form on our website.</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Create plain text content for contact form email
 */
const createContactFormText = (formData: ContactFormData): string => {
  const currentDate = new Date().toLocaleString('en-IE', { 
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  let text = `New Contact Form Submission - The Frosting Room\n\n`;
  text += `You have received a new message through the contact form on The Frosting Room website.\n\n`;
  
  text += `CONTACT DETAILS:\n`;
  text += `Name: ${formData.name}\n`;
  text += `Email: ${formData.email}\n`;
  if (formData.phone) {
    text += `Phone: ${formData.phone}\n`;
  }
  text += `\nMESSAGE:\n${formData.message}\n\n`;
  
  text += `Submitted: ${currentDate}\n\n`;
  text += `This message was sent from the contact form on The Frosting Room website.\n\n`;
  text += `The Frosting Room\n`;
  text += `Dublin, Ireland\n`;
  
  return text;
};

/**
 * Get client IP address (simplified version)
 * In a real implementation, you might want to get this from a service
 */
const getClientIP = async (): Promise<string | undefined> => {
  try {
    // This is a simplified approach - in production you might want to use
    // a service like ipify.org or get it from your server
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not get client IP:', error);
    return undefined;
  }
};


/**
 * Get all contact submissions (for admin use)
 */
export const getContactSubmissions = async (): Promise<ContactSubmission[]> => {
  try {
    const { getDocs, query, orderBy } = await import('firebase/firestore');
    
    const q = query(contactCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
        status: data.status || 'new',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        ipAddress: data.ipAddress,
        userAgent: data.userAgent
      } as ContactSubmission;
    });
  } catch (error) {
    console.error('Error getting contact submissions:', error);
    throw error;
  }
};

/**
 * Update contact submission status (for admin use)
 */
export const updateContactSubmissionStatus = async (
  submissionId: string, 
  status: ContactSubmission['status']
): Promise<void> => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    
    const docRef = doc(db, 'contactSubmissions', submissionId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Contact submission ${submissionId} status updated to ${status}`);
  } catch (error) {
    console.error('Error updating contact submission status:', error);
    throw error;
  }
};
